// src/components/sidebar/Sidebar.js
// ENHANCED: Modern gradient themes, smooth animations, glass morphism effects
// FIXED: Adjusted container width and pointer events to prevent blocking page clicks
// --- UPDATE: passes the signed-in user (getUserInfo) so the sidebar shows the right links / AI pulse

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import Content from "./components/Content";
import { renderThumb, renderTrack, renderView } from "../scrollbar/Scrollbar";
import { Scrollbars } from "react-custom-scrollbars-2";
import { getUserInfo } from "../../utils/auth"; // --- UPDATE

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const sidebarVariants = {
  hidden: { 
    x: -300, 
    opacity: 0,
  },
  visible: { 
    x: 0, 
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 20,
      duration: 0.6,
    }
  },
  exit: {
    x: -300,
    opacity: 0,
    transition: {
      duration: 0.3,
    }
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function Sidebar(props) {
  // Destructure the new 'displaySidebar' prop, defaulting to true
  const { routes, displaySidebar = true } = props;
  const user = useMemo(() => { try { return getUserInfo() || undefined; } catch (_) { return undefined; } }, []); // --- UPDATE

  // ============================================================================
  // ENHANCED THEME COLORS
  // ============================================================================
  let variantChange = "0.3s cubic-bezier(0.4, 0, 0.2, 1)";
  
  // Modern gradient backgrounds
  let sidebarBg = useColorModeValue(
    "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
    "linear-gradient(135deg, rgba(26,32,44,0.98) 0%, rgba(45,55,72,0.95) 100%)"
  );
  
  // Glass morphism shadow
  let shadow = useColorModeValue(
    "0 8px 32px 0 rgba(31, 38, 135, 0.15), 0 0 1px 0 rgba(0,0,0,0.05)",
    "0 8px 32px 0 rgba(0, 0, 0, 0.4), 0 0 1px 0 rgba(255,255,255,0.05)"
  );
  
  // Border with subtle gradient
  let borderColor = useColorModeValue(
    "rgba(226, 232, 240, 0.8)",
    "rgba(74, 85, 104, 0.4)"
  );
  
  // Backdrop blur overlay
  let backdropFilter = "blur(10px) saturate(180%)";

  // Moved hook calls out of JSX
  const accentGradient = useColorModeValue(
    "linear(to-r, blue.400, purple.500, teal.400)",
    "linear(to-r, blue.500, purple.600, teal.500)"
  );

  const orb1Gradient = useColorModeValue(
    "radial(circle, blue.100, transparent)",
    "radial(circle, blue.900, transparent)"
  );

  const orb2Gradient = useColorModeValue(
    "radial(circle, purple.100, transparent)",
    "radial(circle, purple.900, transparent)"
  );
  
  let sidebarMargins = "0px";

  return (
    <AnimatePresence mode="wait">
      {displaySidebar && (
        <MotionBox
          key="sidebar"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={sidebarVariants}
          display={{ base: "none", xl: "block" }}
          // FIX: Changed from "100%" to "300px" to prevent blocking clicks on the main content
          w="300px" 
          position="fixed"
          minH="100%"
          zIndex={10}
          // FIX: Disable pointer events on the container wrapper to allow clicks through transparent areas
          // (Just in case animation scales it momentarily larger)
          pointerEvents="none"
        >
          <Box
            bgGradient={sidebarBg}
            backdropFilter={backdropFilter}
            transition={variantChange}
            w="300px"
            h="100vh"
            m={sidebarMargins}
            minH="100%"
            overflowX="hidden"
            boxShadow={shadow}
            borderRightWidth="1px"
            borderRightColor={borderColor}
            position="relative"
            // FIX: Re-enable pointer events for the actual sidebar content so it is interactive
            pointerEvents="auto" 
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              bgGradient: accentGradient,
              zIndex: 1,
            }}
          >
            <Scrollbars
              autoHide
              renderTrackVertical={renderTrack}
              renderThumbVertical={renderThumb}
              renderView={renderView}
            >
              <Content routes={routes} user={user} />
            </Scrollbars>

            {/* Decorative gradient orbs */}
            <Box
              position="absolute"
              top="20%"
              left="-50px"
              width="150px"
              height="150px"
              bgGradient={orb1Gradient}
              borderRadius="full"
              filter="blur(40px)"
              opacity={0.3}
              pointerEvents="none"
              zIndex={0}
            />
            <Box
              position="absolute"
              bottom="30%"
              right="-30px"
              width="120px"
              height="120px"
              bgGradient={orb2Gradient}
              borderRadius="full"
              filter="blur(35px)"
              opacity={0.3}
              pointerEvents="none"
              zIndex={0}
            />
          </Box>
        </MotionBox>
      )}
    </AnimatePresence>
  );
}

Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  displaySidebar: PropTypes.bool,
  variant: PropTypes.string,
};

export default Sidebar;